import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import {
    WEALTH_EXPENSE_GENERAL_LIVING,
    WEALTH_EXPENSE_INTEREST_BURDEN,
    WEALTH_EXPENSE_OTHER,
    WEALTH_EXPENSE_PREMIUMS,
    WEALTH_EXPENSE_REAL_ESTATE,
    WEALTH_INCOME_EMPLOYMENT,
    WEALTH_INCOME_INSURANCE,
    WEALTH_INCOME_OTHER,
    WEALTH_INCOME_PENSION,
    WEALTH_INCOME_REAL_ESTATE,
    WEALTH_INCOME_SECURITIES,
    WEALTH_INCOME_SHAREHOLDING,
    WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID,
    WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID,
    WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID,
} from '../../../../config/constants';
import type { IMap } from '../../../../interfaces/generic';

declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements {
            'wo-bankable-asset': ClmModalWcProps<ClmBankableAsset>;
            'wo-pension-asset': ClmModalWcProps<null>;
            'wo-insurance-asset': ClmModalWcProps<null>;
            'wo-real-estate-asset': ClmModalWcProps<null>;
            'wo-shareholding-asset': ClmModalWcProps<null>;
            'wo-other-asset': ClmModalWcProps<null>;
            'wo-liability-modal': ClmModalWcProps<null>;
            'wo-expense-modal': ClmModalWcProps<null>;
            'wo-income-modal': ClmModalWcProps<null>;
            'pcm-create-partner-relation-modal': DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
                isPresentationMode?: boolean;
            };
            'pcm-delete-partner-relation-modal': DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {};
            'pcm-update-partner-relation-modal': DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {};
        }
    }
}
export type ClmPartnerType = 'INDIVIDUAL_PARTNER' | 'LEGAL_ENTITY_PARTNER' | undefined;

export type ClmModalActionType = 'CREATE' | 'UPDATE';

export type ClmCustomValidationReturnType = {
    valid: boolean;
    validationMessage: string;
};

export type AssetModalErrorAction = (category: AssetModalErrorCategory, message: string) => void;

export interface IAssetModalProps<WoItemType> {
    actionType: ClmModalActionType;
    partnerType: ClmPartnerType;
    partnerUuid?: string;
    woItemUuid?: string;
    type?: string;
    version?: string;
    onClose: () => void;
    onSubmit: () => void;
    onRemoveWoItem: () => void;
    onError: AssetModalErrorAction;
    validationMessage?: string;
    customValidation?: (action: ClmModalActionType, item: WoItemType) => ClmCustomValidationReturnType;
}

export const wealthCategoryToTypeMap: IMap<string> = {
    // liabilities
    [WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID]: 'MORTGAGE',
    [WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID]: 'LOMBARD_LOAN',
    [WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID]: 'UNSECURED_LOAN',
    [WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID]: 'PRIVATE_LOAN',
    [WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID]: 'POLICY_LOAN',
    [WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID]: 'OTHER_LIABILITY',
    // expenses
    [WEALTH_EXPENSE_PREMIUMS]: 'PREMIUMS',
    [WEALTH_EXPENSE_REAL_ESTATE]: 'REAL_ESTATE_EXPENSES',
    [WEALTH_EXPENSE_GENERAL_LIVING]: 'GENERAL_LIVING',
    [WEALTH_EXPENSE_INTEREST_BURDEN]: 'INTEREST_BURDEN',
    [WEALTH_EXPENSE_OTHER]: 'OTHER_EXPENSES',
    // income
    [WEALTH_INCOME_EMPLOYMENT]: 'EMPLOYMENT',
    [WEALTH_INCOME_OTHER]: 'OTHER_INCOME',
    [WEALTH_INCOME_SECURITIES]: 'SECURITIES_INCOME',
    [WEALTH_INCOME_REAL_ESTATE]: 'REAL_ESTATE_INCOME',
    [WEALTH_INCOME_PENSION]: 'PENSION_INCOME',
    [WEALTH_INCOME_INSURANCE]: 'INSURANCE_INCOME',
    [WEALTH_INCOME_SHAREHOLDING]: 'SHAREHOLDING_INCOME',
};

export type ClmModalWcProps<T> = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> &
    Pick<IAssetModalProps<T>, 'partnerUuid' | 'woItemUuid' | 'partnerType' | 'actionType' | 'type' | 'version'>;

export type AssetModalErrorCategory =
    | 'INTERNAL_ERROR'
    | 'INVALID_VERSION'
    | 'WO_ITEM_NOT_FOUND'
    | 'WO_ITEM_UUID_REQUIRED'
    | 'WO_NOT_FOUND'
    | 'GET_USER_DETAILS_ERROR'
    | 'GET_RATES_ERROR'
    | 'GET_CONFIG_ERROR'
    | 'GET_WEALTH_OVERVIEW_ERROR'
    | 'SAVE_WEALTH_OVERVIEW_ERROR'
    | 'REMOVE_WEALTH_OVERVIEW_ERROR'
    | 'INVALID_ARGUMENTS';

export type ClmBankableAsset = {
    type: string;
    strategy?: string;
};
